/**
 * Image Preloader Utility
 * Preloads images and tracks progress for smooth user experience
 */

export interface PreloadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export type ProgressCallback = (progress: PreloadProgress) => void;

/**
 * Get the full URL with base path for images
 */
const getFullImageUrl = (src: string): string => {
  // If it's already a full URL, return as-is
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }
  
  // Add base URL for relative paths
  const baseUrl = import.meta.env.BASE_URL || '/';
  
  // Remove leading slash from src if present to avoid double slashes
  const cleanSrc = src.startsWith('/') ? src.substring(1) : src;
  
  // Combine base URL and image path
  return `${baseUrl}${cleanSrc}`;
};

/**
 * Preload a single image
 */
const preloadImage = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    const fullUrl = getFullImageUrl(src);
    
    img.onload = () => resolve(src);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    
    img.src = fullUrl;
  });
};

/**
 * Preload multiple images with progress tracking
 */
export const preloadImages = async (
  imageUrls: string[],
  onProgress?: ProgressCallback
): Promise<void> => {
  if (!imageUrls || imageUrls.length === 0) {
    if (onProgress) {
      onProgress({ loaded: 0, total: 0, percentage: 100 });
    }
    return;
  }

  const total = imageUrls.length;
  let loaded = 0;

  // Initial progress
  if (onProgress) {
    onProgress({ loaded: 0, total, percentage: 0 });
  }

  // Create promises for all images
  const promises = imageUrls.map((url) => {
    return preloadImage(url)
      .then(() => {
        loaded++;
        const percentage = Math.round((loaded / total) * 100);
        
        if (onProgress) {
          onProgress({ loaded, total, percentage });
        }
      })
      .catch((error) => {
        console.warn(`Failed to preload image: ${url}`, error);
        loaded++;
        const percentage = Math.round((loaded / total) * 100);
        
        if (onProgress) {
          onProgress({ loaded, total, percentage });
        }
      });
  });

  // Wait for all images to load (or fail)
  await Promise.all(promises);
};

/**
 * Extract all image URLs from configuration
 */
export const extractImageUrls = (config: any): string[] => {
  const urls: string[] = [];

  // Helper to recursively find image URLs
  const findImages = (obj: any) => {
    if (!obj || typeof obj !== 'object') return;

    for (const key in obj) {
      const value = obj[key];

      // Check if it's an image URL
      if (
        typeof value === 'string' &&
        (key.toLowerCase().includes('image') ||
          key.toLowerCase().includes('photo') ||
          key.toLowerCase().includes('logo') ||
          key.toLowerCase().includes('background') ||
          value.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i))
      ) {
        if (value && !value.startsWith('http') && !urls.includes(value)) {
          urls.push(value);
        }
      }
      // Check if it's an array of images
      else if (Array.isArray(value)) {
        value.forEach((item) => {
          if (typeof item === 'string' && item.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
            if (!urls.includes(item)) {
              urls.push(item);
            }
          } else if (typeof item === 'object') {
            findImages(item);
          }
        });
      }
      // Recursively search nested objects
      else if (typeof value === 'object') {
        findImages(value);
      }
    }
  };

  findImages(config);
  return urls;
};

/**
 * Preload all images from configuration
 */
export const preloadConfigImages = async (
  config: any,
  onProgress?: ProgressCallback
): Promise<void> => {
  const imageUrls = extractImageUrls(config);
  
  console.log(`Found ${imageUrls.length} images to preload:`, imageUrls);
  
  await preloadImages(imageUrls, onProgress);
  
  console.log('All images preloaded successfully');
};